import React from 'react';
import { X } from 'lucide-react';

/**
 * Banner — aviso en caja (fondo suave, borde, icono a la izquierda), genérico y
 * reutilizable. Estilo de referencia: AvisoNoAsistio (intervenciones/MisCampanas).
 *
 * Colores desde los tokens semánticos del DS (tokens.css): --{variant}-bg / -fg /
 * base. Se consumen vía `style` con hsl(var(...)) porque el DS los define como
 * variables CSS pero NO están mapeados a utilidades Tailwind en tailwind.config
 * (así se evita hardcodear hex y tocar el config global).
 *
 * props:
 *   variant   'info' | 'warning' | 'success' | 'danger'  (default 'info')
 *   icon      componente de icono (Lucide) opcional
 *   children  texto del aviso
 *   action    { label, onClick } opcional → botón a la derecha
 *   onDismiss () => void opcional → X de cierre (aria-label="Descartar aviso")
 *   className composición
 */
const VARIANTES = {
  info: { bg: '--info-bg', fg: '--info-fg', base: '--info' },
  warning: { bg: '--warning-bg', fg: '--warning-fg', base: '--warning' },
  success: { bg: '--success-bg', fg: '--success-fg', base: '--success' },
  danger: { bg: '--danger-bg', fg: '--danger-fg', base: '--danger' },
};

export function Banner({ variant = 'info', icon: Icon, children, action, onDismiss, className = '' }) {
  const v = VARIANTES[variant] || VARIANTES.info;
  const caja = {
    backgroundColor: `hsl(var(${v.bg}))`,
    color: `hsl(var(${v.fg}))`,
    borderColor: `hsl(var(${v.base}) / 0.4)`,
  };
  return (
    // Móvil: columna (texto arriba, acciones abajo, sin aplastar el texto).
    // Desktop (sm+): una fila (icono · texto · acción · X). Sin animación de entrada.
    <div
      role="status"
      className={`flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 border rounded-lg p-3 text-sm ${className}`}
      style={caja}
    >
      <div className="flex items-start gap-2 min-w-0 flex-1">
        {Icon && <Icon className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />}
        <div className="min-w-0">{children}</div>
      </div>

      {(action || onDismiss) && (
        <div className="flex items-center gap-1 shrink-0 self-end sm:self-auto sm:ml-auto">
          {action && (
            <button
              type="button"
              onClick={action.onClick}
              className="px-3 py-1.5 rounded-md text-xs font-medium border bg-white/60 hover:bg-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
              style={{ borderColor: `hsl(var(${v.base}) / 0.5)`, color: `hsl(var(${v.fg}))` }}
            >
              {action.label}
            </button>
          )}
          {onDismiss && (
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Descartar aviso"
              className="p-1.5 rounded-md hover:bg-black/5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Banner;
